import type { SerializedError } from '@reduxjs/toolkit';
import type AskAllTrailsResponseType from '@alltrails/amplitude/enums/AskAllTrailsResponseType';
import logAskAllTrailsResponseReturned from '@alltrails/amplitude/events/logAskAllTrailsResponseReturned';
import logError from '@alltrails/core/utils/logError';
import type { PageUI, PageUISectionType } from '@alltrails/layout/utils/PageUI/pageUI';
import { baseApi, type BaseArgs, type BaseError } from '@alltrails/redux-helpers/baseApi';
import type { Coordinates } from '@alltrails/types/AlgoliaResults';
import type { RootState } from './redux';
import { setAskChatId } from './slices/askSlice';

export const askAllTrailsUrl = '/api/alltrails/agents/v1/ask-alltrails';

export const askChatsUrl = '/api/alltrails/agents/v1/chats';

export const askSuggestionsUrl = '/api/alltrails/explore/v1/query_suggestions';

export type AskLocation = { topRight: Coordinates; bottomLeft: Coordinates } | { center: Coordinates; maxRadius?: number };

export type AskAllTrailsRequest = {
  /** The user's text, unmodified. 1–1000 characters. */
  query: string;
  /** The viewport when the question was submitted. The server searches here unless the query names a place. */
  location?: AskLocation;
};

/**
 * One cache entry per submitted question. `requestId` is never sent; it keeps each submission its own request, so asking
 * the same question again (ex: after it failed) fetches instead of reusing the old response.
 */
export type AskAllTrailsArgs = {
  /** Whether the question is asked in the Ask chat, which answers it with the earlier turns in mind. Never sent. */
  isChat: boolean;
  requestId: string;
  request: AskAllTrailsRequest;
};

export type AskSuggestionsArgs = {
  limit: number;
  center: Coordinates;
};

type AskResponseAnalytics = {
  response_type: AskAllTrailsResponseType;
  trail_count: number;
  community_insight_count: number;
  error_reason?: string;
};

const countSections = (page: PageUI, sectionType: PageUISectionType) => page.sections.filter(section => section.sectionType === sectionType).length;

type AskChat = {
  id: string;
};

type AskSuggestionsResponse = {
  suggestions: { text: string }[];
  meta: { count: number };
};

export const askAllTrailsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    askAllTrails: builder.query<PageUI, AskAllTrailsArgs>({
      async queryFn({ isChat, request, requestId }, { dispatch, getState }, _extraOptions, baseQuery) {
        const post = async <T>(url: string, data: BaseArgs['data']) => {
          const result = await baseQuery({ url, method: 'post', data });

          return result.error ? { error: result.error as BaseError } : { data: result.data as T };
        };

        if (!isChat) {
          return post<PageUI>(askAllTrailsUrl, request);
        }

        const askIn = (chatId: string) => post<PageUI>(`${askChatsUrl}/${chatId}/messages`, request);

        const { chatId } = (getState() as RootState).ask;

        if (chatId) {
          const answer = await askIn(chatId);

          // A 404 means the chat is gone or isn't this user's, so the question starts a new one
          if (answer.error?.status !== 404) {
            return answer;
          }
        }

        const chat = await post<AskChat>(askChatsUrl, { agent: 'ask_alltrails' });

        if (chat.error) {
          return chat;
        }

        dispatch(setAskChatId({ chatId: chat.data.id, requestId }));

        return askIn(chat.data.id);
      },
      keepUnusedDataFor: 300,
      async onQueryStarted({ requestId }, { getState, queryFulfilled }) {
        // Read at the start, so the response logs the session it was asked in even if Ask closes while it loads
        const { session } = (getState() as RootState).ask;
        const logResponse = (response: AskResponseAnalytics) =>
          logAskAllTrailsResponseReturned({ entry_point: session?.entryPoint, query_id: requestId, session_id: session?.id, ...response });

        await queryFulfilled.then(
          ({ data: page }) => {
            // No results is still a success: a summary saying so, and no trails
            logResponse({
              response_type: 'success',
              trail_count: countSections(page, 'trail-result'),
              community_insight_count: countSections(page, 'community-insight')
            });
          },
          rejection => {
            const status = rejection.isUnhandledError ? undefined : rejection.error.status;

            if (status !== 500 && status !== 504) {
              logError(new Error(`Ask AllTrails request failed with status ${status ?? 'unknown'}`, { cause: rejection.error }));
            }

            logResponse({
              response_type: 'failure',
              trail_count: 0,
              community_insight_count: 0,
              error_reason: rejection.isUnhandledError ? undefined : getAskAllTrailsErrorReason(rejection.error)
            });
          }
        );
      }
    }),
    askSuggestions: builder.query<string[], AskSuggestionsArgs>({
      query: ({ center, limit }) => ({
        url: askSuggestionsUrl,
        config: { params: { limit, 'location[center][lat]': center.lat, 'location[center][lng]': center.lng } }
      }),
      transformResponse: ({ suggestions }: AskSuggestionsResponse) => suggestions.map(({ text }) => text),
      // Kept for the session
      keepUnusedDataFor: Infinity,
      async onQueryStarted(_args, { queryFulfilled }) {
        await queryFulfilled.catch(rejection => {
          const status = rejection.isUnhandledError ? undefined : rejection.error.status;

          logError(new Error(`Ask AllTrails suggestions request failed with status ${status ?? 'unknown'}`, { cause: rejection.error }));
        });
      }
    })
  })
});

export const { useAskAllTrailsQuery, useAskSuggestionsQuery } = askAllTrailsApi;

/**
 * The body of a 500 or 504 error.
 */
type AskAllTrailsServerErrorBody = {
  error: { type: 'agent_failure' | 'timeout'; message: string };
};

/**
 * The body of a 422: the server's validation errors.
 */
type AskAllTrailsValidationErrorBody = {
  detail: { loc: (string | number)[]; msg: string; type: string }[];
};

export type AskAllTrailsError = {
  type?: AskAllTrailsServerErrorBody['error']['type'];
};

export const getAskAllTrailsError = (error: BaseError | SerializedError): AskAllTrailsError => {
  if (!('status' in error)) {
    return {};
  }

  const { error: serverError }: Partial<AskAllTrailsServerErrorBody> = error.data ?? {};

  return { type: serverError?.type };
};

/**
 * The type of the error, from a body the server wrote: `error.type` from a 500 or 504, or the first validation error's type
 * from a 422. Nothing for a response without one (ex: a gateway error, or a network failure, which arrives as a 500 with no body).
 */
export const getAskAllTrailsErrorReason = ({ data }: BaseError): string | undefined => {
  const { detail, error }: Partial<AskAllTrailsServerErrorBody & AskAllTrailsValidationErrorBody> = data ?? {};

  return error?.type ?? detail?.[0]?.type;
};
